import { defineStore } from "pinia";
import { useNuxtApp } from "#app";
import { useAuthStore } from "~/stores/auth";
import { watch } from "vue";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";

const LS_KEY = "wl:variants";

/** Стан стора. Ключ бажаного — рядок "productId:variantId". */
interface WishlistState {
  keys: string[];
  ready: boolean;
  syncing: boolean;
}

// Normalizes wishlist keys. Keeps only valid "pid:vid" strings.
function normalizeKeys(input: unknown): string[] {
  const arr = Array.isArray(input) ? input : [];
  const norm = arr.map((k) => String(k)).filter((k) => !!parseKey(k));
  return Array.from(new Set(norm));
}

const FIELD_SERVER = "wishlistVariantKeys";

const keyOf = (pid: unknown, vid: unknown): string =>
  `${String(pid)}:${String(vid)}`;
const parseKey = (k: unknown): [string, string] | null => {
  if (typeof k !== "string") return null;
  const i = k.indexOf(":");
  if (i <= 0 || i === k.length - 1) return null;
  return [k.slice(0, i), k.slice(i + 1)];
};

/**
 * Форма стора auth у тому обсязі, який потрібен бажаному. Звужуємо явно,
 * бо `stores/auth` — ще .js: його тип виводиться з `ref(null)`, і на
 * `auth.user.uid` покластись не можна.
 */
type AuthLike = { user?: { uid?: unknown } | null } | null;

const uidOf = (auth: AuthLike): string => {
  const raw = auth?.user?.uid;
  return raw ? String(raw) : "";
};

/**
 * uid поточного користувача для дій стора.
 *
 * Замінює вираз `auth?.uid || auth?.user?.uid`, який стояв тут у п'яти
 * копіях. Ліва половина була мертва: стор auth не повертає `uid`
 * (див. його return) — спрацьовувала завжди лише права.
 */
function currentUid(): string {
  return uidOf(useAuthStore() as AuthLike);
}

export const useWishlistStore = defineStore("wishlist", {
  state: (): WishlistState => ({
    keys: [], // ["pid:vid", ...]
    ready: false,
    syncing: false,
  }),
  getters: {
    ids: (s): string[] => (Array.isArray(s.keys) ? s.keys : []),
    count: (s): number => (Array.isArray(s.keys) ? s.keys.length : 0), // ✅ гард
    // Геттери-функції: тип повернення проставлений явно, інакше Pinia
    // не виводить `this.hasPair(...)` всередині дій.
    hasPair:
      (s) =>
      (pid: unknown, vid: unknown): boolean =>
        Array.isArray(s.keys) && s.keys.includes(keyOf(pid, vid)),
    hasVariant:
      (s) =>
      (vid: unknown): boolean =>
        Array.isArray(s.keys) &&
        s.keys.some((k) => (parseKey(k)?.[1] || "") === String(vid || "")),
  },
  actions: {
    loadLocal() {
      if (!import.meta.client) return;
      try {
        // `?? "null"` зберігає стару поведінку: JSON.parse(null) у JS дає
        // null, далі normalizeKeys віддає []. TS такий виклик не пропускає.
        const raw = localStorage.getItem(LS_KEY) ?? "null";
        const arr = JSON.parse(raw);
        this.keys = normalizeKeys(arr);
      } catch {
        this.keys = [];
      }
    },
    saveLocal() {
      if (!import.meta.client) return;
      localStorage.setItem(LS_KEY, JSON.stringify(normalizeKeys(this.keys)));
    },

    async init() {
      this.loadLocal();
      this.ready = true;

      // Стор auth береться ОДИН раз, як і до конвертації: getter watcher'а
      // спрацьовує й поза setup-контекстом, а useAuthStore() там вимагав би
      // активної Pinia.
      const auth = useAuthStore() as AuthLike;

      watch(
        () => uidOf(auth),
        async (uid: string) => {
          if (uid) await this.syncFromServer(uid);
        },
        { immediate: true },
      );
    },

    async syncFromServer(uid: string) {
      try {
        this.syncing = true;
        const { $db } = useNuxtApp();
        const ref = doc($db, "users", uid);
        const snap = await getDoc(ref);

        const serverKeys =
          snap.exists() && Array.isArray(snap.data()[FIELD_SERVER])
            ? snap.data()[FIELD_SERVER].filter((k: unknown) => !!parseKey(k))
            : [];

        const union = normalizeKeys([
          ...(Array.isArray(this.keys) ? this.keys : []),
          ...serverKeys,
        ]);
        this.keys = union;
        this.saveLocal();

        await setDoc(ref, { [FIELD_SERVER]: union }, { merge: true });
      } catch (e) {
        console.warn("wishlist sync failed", e);
      } finally {
        this.syncing = false;
      }
    },

    async addPair(pid: unknown, vid: unknown) {
      const key = keyOf(pid, vid);
      if (!parseKey(key) || (this.keys || []).includes(key)) return;

      this.keys = [key, ...(this.keys || [])];
      this.saveLocal();

      try {
        const uid = currentUid();
        if (!uid) return;
        const { $db } = useNuxtApp();
        await updateDoc(doc($db, "users", uid), {
          [FIELD_SERVER]: arrayUnion(key),
        });
      } catch {
        try {
          const uid = currentUid();
          if (!uid) return;
          const { $db } = useNuxtApp();
          await setDoc(
            doc($db, "users", uid),
            { [FIELD_SERVER]: [key] },
            { merge: true },
          );
        } catch (err) {
          console.warn("wishlist addPair server failed", err);
        }
      }
    },

    async removePair(pid: unknown, vid: unknown) {
      const key = keyOf(pid, vid);
      this.keys = (this.keys || []).filter((k) => k !== key);
      this.saveLocal();

      try {
        const uid = currentUid();
        if (!uid) return;
        const { $db } = useNuxtApp();
        await updateDoc(doc($db, "users", uid), {
          [FIELD_SERVER]: arrayRemove(key),
        });
      } catch (e) {
        console.warn("wishlist removePair server failed", e);
      }
    },

    async removeKeys(keysToRemove: unknown) {
      const list = Array.isArray(keysToRemove)
        ? keysToRemove
        : typeof keysToRemove === "string"
          ? [keysToRemove]
          : [];

      const clean = list.map((k) => String(k)).filter((k) => !!parseKey(k));

      if (!clean.length) return;

      this.keys = normalizeKeys(
        (Array.isArray(this.keys) ? this.keys : []).filter(
          (k) => !clean.includes(k),
        ),
      );
      this.saveLocal();

      try {
        const uid = currentUid();
        if (!uid) return;
        const { $db } = useNuxtApp();
        await updateDoc(doc($db, "users", uid), {
          [FIELD_SERVER]: arrayRemove(...clean),
        });
      } catch {
        // Fallback: ensure server field stays consistent with local
        try {
          const uid = currentUid();
          if (!uid) return;
          const { $db } = useNuxtApp();
          await setDoc(
            doc($db, "users", uid),
            { [FIELD_SERVER]: Array.isArray(this.keys) ? this.keys : [] },
            { merge: true },
          );
        } catch (err) {
          console.warn("wishlist removeKeys server failed", err);
        }
      }
    },

    async togglePair(pid: unknown, vid: unknown) {
      // Було тернарником-виразом заради побічного ефекту; у .js правило
      // no-unused-expressions на нього не діяло, у .ts — діє.
      if (this.hasPair(pid, vid)) {
        await this.removePair(pid, vid);
      } else {
        await this.addPair(pid, vid);
      }
    },
  },
});
